import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Calendar, MapPin, Users, Camera, Ticket, ChevronLeft, Loader2, Heart, Share2, MessageCircle, Instagram, Copy, Music, Tag, Check, Award } from "lucide-react";
import PosterFrame from "@/components/PosterFrame";
import PosterDownload from "@/components/PosterDownload";
import EventMatchStrip from "@/components/EventMatchStrip";


// Deterministic small hash for consistent avatar-image selection per event
function hashCode(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h) + (s || "").charCodeAt(i);
  return Math.abs(h | 0);
}

export default function EventDetail() {
  const params = useParams();
  const { id: rawId, slug: rawSlug } = params;
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [tier, setTier] = useState(null);
  const [qty, setQty] = useState(1);
  const [dayDate, setDayDate] = useState("");  // P2.2 multi-day
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // {code, discount, discount_type, discount_value}
  const [couponBusy, setCouponBusy] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState(0); // Phase 4: loyalty points redemption
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);

  useEffect(() => {
    const fetch = rawSlug
      ? api.get(`/events/slug/${rawSlug}`)
      : api.get(`/events/${rawId}`);
    fetch.then(({ data }) => { setEvent(data); setTier(data.tiers?.[0] || null); if (data.id) api.post(`/events/${data.id}/view`).catch(() => {}); })
      .catch(() => toast.error("Event not found"));
  }, [rawId, rawSlug]);

  useEffect(() => {
    if (user && event?.id) {
      api.get("/favorites/me").then(({ data }) => setIsFav((data.event_ids || []).includes(event.id))).catch(() => {});
    }
  }, [event, user]);

  const eventId = event?.id;

  const toggleFav = async () => {
    if (!user) { toast("Login to save"); return; }
    const { data } = await api.post("/favorites/toggle", { event_id: eventId });
    setIsFav(data.favorited);
    toast.success(data.favorited ? "Saved" : "Removed");
  };

  const register = async () => {
    if (!user) { toast("Please login to register"); nav("/login"); return; }
    setRegistering(true);
    try { await api.post("/registrations", { event_id: eventId }); toast.success("Registered! Check your profile."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not register"); }
    finally { setRegistering(false); }
  };

  const buyTicket = async () => {
    if (!tier) return;
    if (!user) { toast("Please login to book"); nav("/login"); return; }
    // P2.2 multi-day — buyer must pick a day
    const isMulti = !!event?.is_multi_day;
    if (isMulti && !dayDate) { toast.error("Pick which day you're attending"); return; }
    setLoading(true);
    try {
      const payload = { event_id: eventId, tier_name: tier.name, quantity: qty };
      if (couponApplied?.code) payload.coupon_code = couponApplied.code;
      if (redeemPoints >= 100) payload.redeem_points = redeemPoints;
      if (isMulti) payload.day_date = dayDate;
      const { data } = await api.post("/payments/checkout", payload);
      // Razorpay modal
      const options = {
        key: data.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency || "INR",
        name: "Eventom Online",
        description: `${event.title} — ${tier.name} x${qty}`,
        image: "https://storage.fotoowl.ai/cdn-cgi/image/height=200/misc/eventom-logo.png",
        order_id: data.order_id,
        prefill: { name: user.name, email: user.email, contact: user.phone || "" },
        notes: { event_id: eventId, tier: tier.name },
        theme: { color: "#2E6BFF" },
        modal: {
          ondismiss: () => { setLoading(false); toast("Payment cancelled — try again anytime"); },
        },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            nav(`/payment/success?session_id=${resp.razorpay_order_id}`);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
            nav(`/payment/cancel`);
          }
        },
      };
      if (!window.Razorpay) { toast.error("Payment SDK not loaded. Please refresh."); setLoading(false); return; }
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed"); setLoading(false); });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: couponCode.trim(),
        event_id: eventId,
        base_amount: subtotal,
      });
      if (!data.valid) { toast.error(data.message || "Invalid coupon"); setCouponApplied(null); return; }
      setCouponApplied(data);
      toast.success(`Coupon applied — you saved ₹${data.discount}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Coupon check failed");
    } finally {
      setCouponBusy(false);
    }
  };

  if (!event) return <Layout><div className="max-w-7xl mx-auto p-10 font-display text-3xl uppercase">Loading...</div></Layout>;
  const startDate = event.start_date || event.date;
  const endDate = event.end_date;
  const dateStr = new Date(startDate).toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const endDateStr = endDate && endDate !== startDate ? new Date(endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
  const eventEnd = new Date(endDate || event.date); eventEnd.setHours(23, 59, 59, 999);
  const eventIsPast = eventEnd < new Date();
  const subtotal = (tier?.price || 0) * qty;
  // P2.7 3.6% platform fee shown to buyer; Phase 3: coupon discount is applied to base before fee; Phase 4: loyalty points too
  const PLATFORM_FEE_PCT = 0.036;
  const couponDiscount = couponApplied?.discount || 0;
  const totalDiscount = couponDiscount + (loyaltyDiscount || 0);
  const discountedBase = Math.max(0, Math.round((subtotal - totalDiscount) * 100) / 100);
  const platformFee = Math.round(discountedBase * PLATFORM_FEE_PCT * 100) / 100;
  const total = Math.round((discountedBase + platformFee) * 100) / 100;
  const eventType = event.event_type || "ticketed";
  const isFree = eventType === "free";
  const isInvite = eventType === "invite_only";
  const isTicketed = eventType === "ticketed";
  const bookingMode = event.booking_mode || "eventom";
  const externalLinks = Array.isArray(event.external_booking_links) ? event.external_booking_links.filter((l) => l && l.url) : [];
  const isDiscoveryOnly = bookingMode === "discovery_only";
  const showEventomBook = isTicketed && (bookingMode === "eventom");
  const artists = Array.isArray(event.artists) ? event.artists.filter((a) => a && a.name) : [];
  const isExperience = (event.type || "event") === "experience";
  const shareUrl = event.slug
    ? `${window.location.origin}/${isExperience ? "experiences" : "e"}/${event.slug}`
    : `${window.location.origin}/events/${event.id}`;
  const shareText = `${event.title} — ${dateStr} · ${event.city || event.location || ""} | Book on Eventom`;

  const shareOnWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank", "noopener");
  };
  const shareOnInstagram = async () => {
    // Instagram has no direct share URL — copy the link + open IG
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — paste in your Insta story/DM"); }
    catch { toast("Copy manually: " + shareUrl); }
    window.open("https://www.instagram.com/", "_blank", "noopener");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); }
    catch { toast(shareUrl); }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: event.title, text: shareText, url: shareUrl }); return; }
      catch {}
    }
    setShowShare(true);
  };

  return (
    <Layout>
      <div className="relative">
        <div className="relative h-[420px] md:h-[560px] w-full overflow-hidden">
          {event.banner ? (
            <img src={event.banner} alt={event.title} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0"><PosterFrame event={event} /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/60 to-bg/20" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 max-w-7xl mx-auto">
            <Link to="/" data-testid="event-back" className="inline-flex items-center gap-1 text-white/85 font-mono text-xs uppercase tracking-widest mb-3 hover:text-lime"><ChevronLeft className="w-4 h-4" /> Back</Link>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-block bg-magenta text-white px-3 py-1 rounded-full font-mono text-[10px] tracking-widest uppercase">#Eventom{event.category ? `${(event.category || "").replace(/\s+/g,"")}` : "Exclusive"}</span>
              <span className="inline-block bg-lime text-ink px-3 py-1 rounded-full font-mono text-[10px] tracking-widest uppercase" data-testid="event-type-badge">{isExperience ? "Experience" : "Event"}</span>
              {endDateStr && <span className="inline-block bg-white/10 text-white px-3 py-1 rounded-full font-mono text-[10px] tracking-widest uppercase" data-testid="event-multiday-badge">Multi-day</span>}
            </div>
            <h1 className="font-fancy text-4xl sm:text-5xl md:text-6xl leading-[0.95] max-w-4xl text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]" data-testid="event-title">{event.title}</h1>
            {event.subtitle && <div className="mt-2 text-lg md:text-xl text-white/85 max-w-3xl">{event.subtitle}</div>}
            {(Number(event.going_count) > 0 || Number(event.tickets_left) > 0) && !eventIsPast && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {Number(event.going_count) > 0 && (
                  <div className="inline-flex items-center gap-2 bg-black/40 backdrop-blur border border-white/15 rounded-full pl-1 pr-3 py-1" data-testid="event-going-badge">
                    <div className="flex -space-x-2">
                      {[0,1,2,3].map((i) => (
                        <div key={i} className="w-6 h-6 rounded-full border-2 border-black/40 bg-cover bg-center" style={{ backgroundImage: `url(https://i.pravatar.cc/40?img=${(hashCode(event.id || "e") + i) % 70 + 1})` }} />
                      ))}
                    </div>
                    <span className="text-white font-mono text-[11px] font-bold uppercase tracking-widest">{Number(event.going_count).toLocaleString("en-IN")}+ people going</span>
                  </div>
                )}
                {Number(event.tickets_left) > 0 && (
                  <span className="inline-flex items-center gap-1.5 bg-magenta text-white px-3.5 py-1.5 rounded-full font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="event-selling-fast">
                    Selling fast — {Number(event.tickets_left)} left
                  </span>
                )}
                {user && (
                  <Link to={`/clubvip?event=${event.id}`} data-testid="event-open-in-match" className="inline-flex items-center gap-1.5 bg-skyx/20 hover:bg-skyx/30 text-skyx border border-skyx/40 px-3.5 py-1.5 rounded-full font-mono text-[11px] font-bold uppercase tracking-widest transition-colors">
                    <Heart className="w-3 h-3" /> Open in Match
                  </Link>
                )}
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-4 text-white/85 font-mono text-xs uppercase tracking-widest">
              <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4 text-lime" /> {dateStr}{endDateStr ? ` → ${endDateStr}` : ""}</span>
              {(event.time || event.end_time) && <span className="inline-flex items-center gap-1">{event.time || "TBD"}{event.end_time ? ` – ${event.end_time}` : ""} IST</span>}
              {event.maps_link ? (
                <a href={event.maps_link} target="_blank" rel="noreferrer" data-testid="event-maps-link" className="inline-flex items-center gap-1 hover:text-lime underline decoration-dotted"><MapPin className="w-4 h-4 text-magenta" /> {event.location_name || event.location || "View on Maps"}</a>
              ) : (
                <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-magenta" /> {event.location}</span>
              )}
              <span className="inline-flex items-center gap-1"><Users className="w-4 h-4 text-orangex" /> {event.organizer_name}</span>
              <button onClick={nativeShare} data-testid="event-share-btn" className="inline-flex items-center gap-1 hover:text-lime"><Share2 className="w-4 h-4 text-lime" /> Share</button>
              <PosterDownload event={event} testId="event-poster-inline" label="Poster" className="inline-flex items-center gap-1 hover:text-skyx" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="glass rounded-3xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-lime mb-2">About the event</div>
              {user && (
                <button data-testid="event-fav-btn" onClick={toggleFav} className={`icon-circle !w-10 !h-10 ${isFav ? "bg-magenta text-white" : "bg-white/5 text-white"}`}><Heart className="w-4 h-4" fill={isFav ? "currentColor" : "none"} /></button>
              )}
            </div>
            <p className="text-lg font-body leading-relaxed text-text/85">{event.description}</p>
            {event.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {event.tags.map((t) => <span key={t} className="text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-lime/10 text-lime border border-lime/30">#{t}</span>)}
              </div>
            )}
          </div>

          {artists.length > 0 && (
            <div className="glass rounded-3xl p-6" data-testid="event-artists-section">
              <div className="font-mono text-[10px] uppercase tracking-widest text-lime mb-3">Lineup</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {artists.map((a, i) => (
                  <div key={i} className="text-center" data-testid={`event-artist-${i}`}>
                    <div className="aspect-square rounded-2xl overflow-hidden bg-white/5 border border-white/10 mb-2">
                      {a.photo ? <img src={a.photo} alt={a.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Music className="w-8 h-8 text-lime/60" /></div>}
                    </div>
                    <div className="font-display text-base uppercase leading-tight">{a.name}</div>
                    {a.genre && <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-0.5">{a.genre}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hooked-style: see who's going + match before you meet */}
          <EventMatchStrip event={event} />

          {Array.isArray(event.sponsors) && event.sponsors.filter((s) => s && s.name).length > 0 && (
            <div className="glass rounded-3xl p-6" data-testid="event-sponsors-section">
              <div className="font-mono text-[10px] uppercase tracking-widest text-purple mb-3">Sponsors & Partners</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {event.sponsors.filter((s) => s && s.name).map((s, i) => (
                  <div key={i} className="glass !rounded-2xl p-3 text-center" data-testid={`event-sponsor-${i}`}>
                    {s.type && <div className="text-[9px] font-mono uppercase tracking-widest text-muted mb-1">{s.type}</div>}
                    {s.logo_url ? (
                      <div className="aspect-square rounded-xl bg-white/95 flex items-center justify-center overflow-hidden mb-2 p-2">
                        <img src={s.logo_url} alt={s.name} className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="aspect-square rounded-xl bg-white/5 border border-white/10 mb-2 flex items-center justify-center font-display text-2xl">{s.name[0]}</div>
                    )}
                    <div className="font-heading font-bold text-xs leading-tight">{s.name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(event.last_year_media) && event.last_year_media.filter((m) => m && m.url).length > 0 && (
            <div className="glass rounded-3xl p-6" data-testid="event-last-year-section">
              <div className="font-mono text-[10px] uppercase tracking-widest text-orangex mb-3">Last year at this event</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {event.last_year_media.filter((m) => m && m.url).map((m, i) => {
                  const url = m.url;
                  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
                  const ig = /instagram\.com\/(p|reel|tv)\//.test(url);
                  const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
                  return (
                    <div key={i} className="rounded-2xl overflow-hidden bg-black border border-white/10 aspect-video" data-testid={`event-lastyear-${i}`}>
                      {yt ? (
                        <iframe title={`ly-${i}`} src={`https://www.youtube.com/embed/${yt[1]}`} className="w-full h-full" allowFullScreen frameBorder="0" />
                      ) : ig ? (
                        <iframe title={`ly-${i}`} src={`${url.replace(/\/$/, "")}/embed`} className="w-full h-full" frameBorder="0" scrolling="no" allowTransparency />
                      ) : isImg || !/^https?:\/\//.test(url) ? (
                        <img src={url} alt={m.caption || `Last year ${i + 1}`} className="w-full h-full object-cover" />
                      ) : (
                        <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex items-center justify-center text-xs font-mono uppercase text-lime">Open link ↗</a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(event.ai_photo_gallery_url || event.external_gallery_url || event.ai_gallery_qr_path) && (
            <div className="rounded-3xl p-6 bg-surface border border-line" data-testid="event-ai-gallery-section">
              <div className="font-mono text-[10px] uppercase tracking-widest text-lime mb-2">AI Photo Gallery</div>
              <div className="font-display text-3xl uppercase leading-tight">Photos from this event are <span className="grad-text-hot">live.</span></div>
              <p className="text-text/80 mt-2">Selfie inside Eventom AI → every shot of you, in seconds.</p>
              <div className="mt-4 flex flex-wrap items-start gap-4">
                {(event.ai_photo_gallery_url || event.external_gallery_url) && (
                  <a href={event.ai_photo_gallery_url || event.external_gallery_url} target="_blank" rel="noreferrer" data-testid="event-photos-link" className="btn-primary"><Camera className="w-4 h-4" /> Access Photos →</a>
                )}
                {event.ai_gallery_qr_path && (
                  <div className="glass rounded-2xl p-3 flex flex-col items-center" data-testid="event-gallery-qr">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${event.ai_gallery_qr_path}`} alt="Gallery QR" className="w-32 h-32 bg-white p-1 rounded-lg" />
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-2">Scan for gallery</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1 space-y-4">
          {/* Eventom Match opt-in card — open to any signed-in user attending (paid or RSVP). */}
          {user && (
            <MatchmakingCard eventId={eventId} />
          )}
          {/* Tag Your Squad — mockup-inspired share card */}
          <div className="rounded-3xl overflow-hidden relative border border-white/10 bg-gradient-to-br from-magenta/25 via-purple/20 to-lime/10 p-5" data-testid="event-tag-squad">
            <div className="text-[10px] font-mono uppercase tracking-widest text-magenta">Bring your people</div>
            <div className="font-fancy text-3xl text-white leading-[1] mt-1">Tag your squad<br/><span className="grad-text-hot">and get ready.</span></div>
            <p className="text-xs text-white/70 mt-3">₹100 off per friend you bring. Free ticket at 3 conversions.</p>
            <div className="mt-4 flex gap-2">
              <button onClick={nativeShare} className="px-3 py-2 rounded-full text-[10px] font-heading font-bold uppercase tracking-widest bg-lime text-ink hover:bg-lime/90 inline-flex items-center gap-1.5" data-testid="event-share-squad"><Share2 className="w-3 h-3" /> Share</button>
              <Link to="/leaderboard" className="px-3 py-2 rounded-full text-[10px] font-heading font-bold uppercase tracking-widest bg-white/10 text-white border border-white/20 hover:bg-white/20 inline-flex items-center gap-1.5" data-testid="event-squad-board"><Award className="w-3 h-3" /> Leaderboard</Link>
            </div>
            <div className="pointer-events-none absolute -right-4 -bottom-4 font-script text-3xl text-white/40 -rotate-6">Good People<br/>Better Events</div>
          </div>
          <div className="sticky top-24 glass rounded-3xl overflow-hidden">
            <div className={`${eventIsPast ? "bg-magenta text-white" : isFree ? "bg-lime !text-ink" : isInvite ? "bg-surface border-b border-line text-text" : "bg-magenta text-white"} px-5 py-3 font-mono text-xs uppercase tracking-widest flex items-center gap-2`}>
              <Ticket className="w-4 h-4" /> {eventIsPast ? "Sold Out — Event Ended" : isFree ? "Free RSVP" : isInvite ? "Invite Only" : "Grab a ticket"}
            </div>
            <div className="p-5">
              {eventIsPast ? (
                <div className="py-6 text-center">
                  <div className="font-display text-4xl uppercase text-magenta leading-none" data-testid="event-sold-out">Sold out.</div>
                  <p className="mt-3 text-sm text-text/70">This event has already happened. Bookings are closed. Explore upcoming events on the home page.</p>
                  <Link to="/" data-testid="event-past-back" className="mt-4 btn-lime justify-center w-full">See Upcoming Events</Link>
                </div>
              ) : isInvite ? (
                <div className="py-6 text-center" data-testid="event-invite-only">
                  <div className="font-display text-3xl uppercase leading-none">Invite only.</div>
                  <p className="mt-3 text-sm text-text/70">This event is by invitation only. Reach out to the organiser directly if you're interested.</p>
                </div>
              ) : isFree ? (
                <div className="py-2" data-testid="event-free">
                  <div className="font-display text-3xl uppercase leading-none">Free entry.</div>
                  <p className="mt-2 text-sm text-text/70">No ticket required — just RSVP to save your spot.</p>
                </div>
              ) : (
                <>
                  {/* P2.2 Multi-day — day pill picker above tiers */}
                  {event.is_multi_day && Array.isArray(event.day_dates) && event.day_dates.length > 1 && (
                    <div className="mb-4" data-testid="event-day-picker">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-muted mb-2">Pick your day</div>
                      <div className="flex flex-wrap gap-2">
                        {event.day_dates.map((iso) => {
                          const d = new Date(iso);
                          const label = `${d.toLocaleDateString("en-IN", { weekday: "short" })} ${d.toLocaleDateString("en-IN", { day: "2-digit" })}`;
                          const active = dayDate === iso;
                          return (
                            <button key={iso} type="button" onClick={() => setDayDate(iso)}
                              data-testid={`event-day-pill-${iso}`}
                              className={`px-3 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-widest border transition-colors ${active ? "bg-lime text-ink border-lime" : "border-white/15 text-white/80 hover:border-white/40"}`}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      {!dayDate && <div className="text-[10px] font-mono text-magenta mt-2">Tap a day to continue</div>}
                    </div>
                  )}
                  <div className="space-y-2">
                    {event.tiers?.map((t) => (
                      <button key={t.name} data-testid={`tier-${t.name.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => setTier(t)}
                        className={`w-full text-left flex items-center justify-between px-3 py-3 rounded-2xl border ${tier?.name === t.name ? "border-lime bg-lime/10" : "border-white/10 bg-white/5"}`}>
                        <div>
                          <div className="font-bold uppercase text-sm tracking-wider">{t.name}</div>
                          <div className="text-xs font-mono text-muted">{t.quantity} available</div>
                        </div>
                        <div className="font-display text-2xl">₹{t.price}</div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <label className="font-mono text-xs uppercase tracking-widest text-muted">Qty</label>
                    <input type="number" min={1} max={20} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(20, Number(e.target.value))))} className="input-dark !py-1 w-20 !rounded-lg font-mono" data-testid="ticket-qty" />
                  </div>

                  <div className="mt-5 pt-4 border-t border-white/10 font-mono text-xs uppercase tracking-widest space-y-1">
                    <div className="flex justify-between text-muted"><span>Subtotal</span><span data-testid="event-subtotal">₹{subtotal.toFixed(2)}</span></div>
                    {couponApplied && (
                      <div className="flex justify-between text-lime" data-testid="event-coupon-discount"><span>Coupon {couponApplied.code}</span><span>− ₹{Number(couponApplied.discount).toFixed(2)}</span></div>
                    )}
                    {loyaltyDiscount > 0 && (
                      <div className="flex justify-between text-lime" data-testid="event-loyalty-discount"><span>Loyalty ({redeemPoints} pts)</span><span>− ₹{Number(loyaltyDiscount).toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between text-muted"><span>Platform fee (3.6%)</span><span data-testid="event-platform-fee">₹{platformFee.toFixed(2)}</span></div>
                    <div className="flex justify-between text-white text-base pt-1"><span>Total</span><span className="font-display text-2xl" data-testid="event-total">₹{total.toFixed(2)}</span></div>
                  </div>

                  {/* Phase 4: Loyalty points redemption */}
                  {user && (user.loyalty_points || 0) >= 100 && (
                    <div className="mt-3 pt-3 border-t border-white/10" data-testid="event-loyalty-section">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-lime"><Award className="w-3 h-3" /> Loyalty points ({user.loyalty_points})</div>
                        {loyaltyDiscount > 0 && <button onClick={() => { setRedeemPoints(0); setLoyaltyDiscount(0); }} data-testid="event-loyalty-remove" className="text-[10px] font-mono uppercase text-magenta underline">Remove</button>}
                      </div>
                      {loyaltyDiscount === 0 ? (
                        <div className="flex items-center gap-2">
                          <select value={redeemPoints} onChange={(e) => setRedeemPoints(Number(e.target.value))} className="input-dark !py-2 !text-xs flex-1" data-testid="event-loyalty-select">
                            <option value={0} className="bg-bg">Don't redeem</option>
                            {[100, 200, 300, 500, 1000].filter((v) => v <= (user.loyalty_points || 0) && (v / 100) * 10 < subtotal).map((v) => (
                              <option key={v} value={v} className="bg-bg">Redeem {v} pts (₹{(v / 100) * 10} off)</option>
                            ))}
                          </select>
                          <button disabled={redeemPoints < 100} onClick={async () => {
                            try {
                              const { data } = await api.post("/loyalty/quote", { event_id: eventId, points: redeemPoints, base_amount: subtotal });
                              setLoyaltyDiscount(data.discount);
                              toast.success(`${data.points_redeemed} pts applied · ₹${data.discount} off`);
                            } catch (e) { toast.error("Failed"); }
                          }} data-testid="event-loyalty-apply" className="btn-lime !py-2 !px-3 !text-xs">Apply</button>
                        </div>
                      ) : (
                        <div className="rounded-2xl bg-lime/10 border border-lime/30 p-2.5 text-[11px] font-mono text-lime" data-testid="event-loyalty-applied">✓ {redeemPoints} pts locked in — ₹{loyaltyDiscount} off</div>
                      )}
                    </div>
                  )}

                  {/* Phase 3: Coupon input */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    {couponApplied ? (
                      <div className="flex items-center justify-between rounded-2xl bg-lime/10 border border-lime/30 p-3" data-testid="event-coupon-applied">
                        <div className="flex items-center gap-2 min-w-0">
                          <Check className="w-4 h-4 text-lime shrink-0" />
                          <div className="min-w-0">
                            <div className="font-bold uppercase text-sm tracking-wider text-lime truncate">{couponApplied.code}</div>
                            <div className="text-[10px] font-mono text-muted">You saved ₹{Number(couponApplied.discount).toFixed(2)}</div>
                          </div>
                        </div>
                        <button onClick={() => { setCouponApplied(null); setCouponCode(""); }} data-testid="event-coupon-remove" className="text-magenta text-[10px] font-mono uppercase tracking-widest underline">Remove</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Tag className="w-4 h-4 text-lime absolute left-3 top-1/2 -translate-y-1/2" />
                          <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="event-coupon-input" className="input-dark !pl-10 !py-2 !text-sm w-full uppercase tracking-widest font-mono" />
                        </div>
                        <button onClick={applyCoupon} disabled={couponBusy || !couponCode.trim()} data-testid="event-coupon-apply" className="btn-lime !py-2 !px-4 !text-xs">
                          {couponBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {!eventIsPast && !isInvite && (
              <div className="p-4 flex flex-col gap-2 pt-0">
                {showEventomBook && (
                  <button onClick={buyTicket} disabled={loading || !tier} data-testid="buy-ticket-btn" className="btn-pink justify-center !py-3">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} {loading ? "Redirecting..." : "Book Ticket"}
                  </button>
                )}
                {/* External booking buttons — one per configured provider */}
                {externalLinks.length > 0 && (
                  <>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">Book from</div>
                    {externalLinks.map((l, i) => (
                      <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`ext-book-${(l.provider||'link').toLowerCase().replace(/\s+/g,'-')}`}
                        className="btn-primary justify-center !py-3">
                        <Ticket className="w-4 h-4" /> {l.provider || "Book externally"} →
                      </a>
                    ))}
                  </>
                )}
                {/* Free RSVP button — ONLY on truly free events (item 5) */}
                {isFree && (
                  <button onClick={register} disabled={registering} data-testid="register-btn" className="btn-lime justify-center !py-3">
                    {registering ? "Registering..." : "RSVP (Free)"}
                  </button>
                )}
                {/* Discovery-only events (no purchase through Eventom, no external link either) */}
                {isDiscoveryOnly && externalLinks.length === 0 && (
                  <div className="text-xs font-mono text-muted text-center py-2">This event is listed for discovery — check with the organiser directly for booking.</div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* STICKY BOTTOM CTA — price left, lime Book Now right, reachable from any scroll depth */}
      {!eventIsPast && !isInvite && (showEventomBook || isFree) && (
        <>
          <div className="h-24" aria-hidden />
          <div className="sticky-cta bottom-[68px] md:bottom-0" data-testid="event-sticky-cta">
            <div className="max-w-7xl mx-auto flex-1 flex items-center justify-between gap-3 w-full px-2">
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{isFree ? "Entry" : tier ? `${tier.name} × ${qty} · incl. fees` : "Select a tier"}</div>
                <div className="font-mono font-bold text-xl text-lime" data-testid="sticky-cta-price">{isFree ? "FREE" : `₹${total.toFixed(0)}`}</div>
              </div>
              {isFree ? (
                <button onClick={register} disabled={registering} data-testid="sticky-cta-book" className="btn-lime !py-3 !px-8">
                  {registering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} RSVP Free
                </button>
              ) : (
                <button onClick={buyTicket} disabled={loading || !tier} data-testid="sticky-cta-book" className="btn-lime !py-3 !px-8">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Book Now
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {showShare && (
        <div className="fixed inset-0 z-50 bg-bg/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4" onClick={() => setShowShare(false)}>
          <div className="glass rounded-3xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="event-share-modal">
            <div className="font-display text-2xl uppercase mb-4">Share this {isExperience ? "experience" : "event"}</div>
            <div className="grid grid-cols-3 gap-3">
              <button onClick={shareOnWhatsApp} data-testid="share-whatsapp" className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10">
                <MessageCircle className="w-6 h-6 text-lime" />
                <span className="text-[10px] font-mono uppercase tracking-widest">WhatsApp</span>
              </button>
              <button onClick={shareOnInstagram} data-testid="share-instagram" className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10">
                <Instagram className="w-6 h-6 text-magenta" />
                <span className="text-[10px] font-mono uppercase tracking-widest">Instagram</span>
              </button>
              <button onClick={copyLink} data-testid="share-copy" className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10">
                <Copy className="w-6 h-6 text-skyx" />
                <span className="text-[10px] font-mono uppercase tracking-widest">Copy link</span>
              </button>
            </div>
            <div className="mt-3 pt-3 border-t border-white/10">
              <PosterDownload event={event} testId="event-poster-download" label="Download poster for Instagram"
                className="w-full justify-center btn-ghost !py-2.5 !text-xs" />
            </div>
            <div className="mt-4 text-[10px] font-mono text-muted break-all">{shareUrl}</div>
            <button onClick={() => setShowShare(false)} className="mt-4 w-full btn-ghost justify-center">Close</button>
          </div>
        </div>
      )}

      <AsSeenAtStrip eventId={event.id} />
    </Layout>
  );
}

function AsSeenAtStrip({ eventId }) {
  const [products, setProducts] = React.useState([]);
  React.useEffect(() => {
    import("@/lib/api").then(({ api }) => api.get(`/shop/event/${eventId}/products`).then(({ data }) => setProducts(data?.products || [])).catch(() => {}));
  }, [eventId]);
  if (!products.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12" data-testid="event-as-seen-at">
      <div className="mb-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-lime">As seen at this event</div>
        <h2 className="font-fancy text-2xl md:text-3xl text-white">Shop the vibe.</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {products.map((p) => (
          <a key={p.id} href={p.affiliate_url || "/shop"} target={p.affiliate_url ? "_blank" : "_self"} rel="noreferrer"
             data-testid={`as-seen-p-${p.id}`}
             className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] block hover:border-lime/40 transition-colors">
            <div className="aspect-square overflow-hidden">
              <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-2">
              <div className="text-[10px] font-heading font-bold text-white line-clamp-2 leading-tight">{p.title}</div>
              <div className="text-[11px] font-mono text-lime mt-1">₹{Number(p.price).toLocaleString("en-IN")}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function MatchmakingCard({ eventId }) {
  // Standalone Match CTA — no VIP gate, no opt-in call. Sends the user to
  // /clubvip scoped to this event so they can build/verify their profile
  // and see other attendees (paid + RSVP) going to the same event.
  const [stats, setStats] = useState({ going: 0, verified_singles: 0, my_ticket: false, preview_avatars: [] });
  useEffect(() => {
    if (!eventId) return;
    api.get(`/clubvip/event-stats/${eventId}`).then(({ data }) => setStats(data)).catch(() => {});
  }, [eventId]);

  const avatars = stats.preview_avatars || [];
  const names = avatars.map((a) => a.first_name).slice(0, 2);
  const remaining = Math.max(0, (stats.verified_singles || 0) - names.length);
  const initial = (n) => (n || "F").trim().charAt(0).toUpperCase();

  return (
    <div className="glass rounded-3xl p-4 mb-4 relative overflow-hidden" data-testid="matchmaking-card">
      <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-magenta/20 blur-3xl" />
      <span className="pointer-events-none absolute -left-6 -bottom-6 w-28 h-28 rounded-full bg-lime/15 blur-2xl" />
      <div className="relative">
        <div className="chip !border-magenta/50 !text-magenta mb-2">Eventom Match</div>
        <div className="font-display uppercase text-lg leading-tight mb-2">Meet people going here</div>

        {avatars.length > 0 ? (
          <div className="mb-3">
            <div className="flex items-center -space-x-2.5" data-testid="matchmaking-avatars">
              {avatars.map((a, i) => (
                <div key={a.user_id} className="relative w-9 h-9 rounded-full border-2 border-bg overflow-hidden" style={{ zIndex: 10 - i }} data-testid={`matchmaking-avatar-${i}`}>
                  {a.hero ? (
                    <img src={a.hero} alt={a.first_name} className="w-full h-full object-cover blur-[3px] scale-110" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center text-[10px] font-bold text-white bg-gradient-to-br ${i % 3 === 0 ? "from-magenta to-pink" : i % 3 === 1 ? "from-skyx to-purplex" : "from-lime to-orangex"}`}>{initial(a.first_name)}</div>
                  )}
                </div>
              ))}
              {remaining > 0 && (
                <div className="relative w-9 h-9 rounded-full border-2 border-bg bg-white/10 backdrop-blur flex items-center justify-center text-[9px] font-mono font-bold text-white" style={{ zIndex: 5 }}>+{remaining}</div>
              )}
            </div>
            <div className="text-[11px] text-white/90 mt-2">
              <span className="text-lime">{names.join(", ") || "Fans"}</span>
              {remaining > 0 && <> +{remaining} more going</>}
            </div>
          </div>
        ) : (
          <div className="text-[11px] text-muted mb-3">
            {stats.verified_singles > 0
              ? <>{stats.verified_singles} verified single{stats.verified_singles === 1 ? "" : "s"} attending{stats.matches ? ` · ${stats.matches} match${stats.matches === 1 ? "" : "es"}` : ""}</>
              : <>Open to every fan — book a ticket or RSVP to unlock event-scoped matching.</>}
          </div>
        )}

        <Link
          to={`/clubvip?event=${eventId}`}
          data-testid="matchmaking-toggle"
          className="btn-pink w-full justify-center !py-2 !text-xs"
        >
          {stats.my_ticket ? "Open Match for this event →" : avatars.length ? "See who's going →" : "Explore Eventom Match →"}
        </Link>
      </div>
    </div>
  );
}

